/**
 * B.3 — Auto-approve evaluator for NIPE-proposed SharePoint writes.
 *
 * Pure function. Given a pending write's folderPath + the list of
 * enabled rules for the portal, decide whether the write should skip
 * the human-approval tray and go straight to lane B (per
 * src/lib/writes/lanes.md).
 *

 * Matchers (first version, per plan Q2.3):
 *   - Folder-path glob only (e.g. Drafts followed by globstar, or
 *     Matters/<id>/Drafts/<everything>)
 *
 * Future matchers (F-AUTO-APPROVE-RULE-UI in CURRENT-BACKLOG.md):
 *   - Write kind (save-back vs create-link vs invite)
 *   - Size threshold
 *   - NIPE-authored vs human-authored
 *
 * Leaf module — no React, no Convex, no Graph imports.
 */

export interface PendingWriteCandidate {
  /** Drive-relative folder path (e.g. `Matters/Acme/Drafts`). */
  folderPath: string;
}

export interface ApprovalRule {
  _id: string;
  folderGlob: string;
  label: string;
}

export interface AutoApproveResult {
  shouldAutoApprove: boolean;
  /** Id of the rule that matched, or null if no match. */
  matchedRuleId: string | null;
  /** Human-readable label of the matched rule (for audit + UI). */
  matchedRuleLabel: string | null;
}

const NO_MATCH: AutoApproveResult = {
  shouldAutoApprove: false,
  matchedRuleId: null,
  matchedRuleLabel: null,
};

/**
 * Glob → RegExp. Supports:
 *   - `*`  matches any sequence of characters except `/`
 *   - `**` matches any sequence of characters including `/`
 *   - `?`  matches a single character except `/`
 *   - everything else is literal (RegExp metas escaped)
 *
 * The match is implicitly anchored to the whole path
 * (`folderPath` exactly matches the glob — no trailing slash drift).
 */
export function globToRegExp(glob: string): RegExp {
  // First, strip leading `/` so `/Drafts/**` and `Drafts/**` behave the same.
  const trimmed = glob.replace(/^\/+/, '');
  // Escape RegExp metas EXCEPT * ? which we expand below.
  const escaped = trimmed.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  // Replace `**` first (greedy / cross-slash) before `*` (segment-only).
  const expanded = escaped
    .replace(/\*\*/g, ' ') // placeholder for **
    .replace(/\*/g, '[^/]*')
    .replace(/\?/g, '[^/]')
    .replace(/ /g, '.*');
  return new RegExp(`^${expanded}$`);
}

/**
 * Returns true if `folderPath` matches the given `glob`.
 * Normalizes leading slashes on both inputs so users can write either
 * `/Drafts/**` or `Drafts/**` in the rule and either path style at the
 * call site.
 */
export function matchesGlob(folderPath: string, glob: string): boolean {
  const normalizedPath = folderPath.replace(/^\/+/, '');
  return globToRegExp(glob).test(normalizedPath);
}

/**
 * Evaluate the pending write against the enabled rule list. Returns
 * the FIRST matching rule (stable order = rule list order, which the
 * convex query already returns by createdAt).
 *
 * Empty rules list ⇒ never auto-approve.
 */
export function evaluateAutoApprove(
  candidate: PendingWriteCandidate,
  rules: ReadonlyArray<ApprovalRule>,
): AutoApproveResult {
  for (const rule of rules) {
    if (matchesGlob(candidate.folderPath, rule.folderGlob)) {
      return {
        shouldAutoApprove: true,
        matchedRuleId: rule._id,
        matchedRuleLabel: rule.label,
      };
    }
  }
  return NO_MATCH;
}
